import { CommonModule, KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnChanges } from '@angular/core';
import { ReteModule } from 'rete-angular-plugin/15';
import { NodeEvent } from '../../models/node-event';
import { NodeEventTypes } from '../../models/node-event-types';
import { WorkflowTriggerNode } from '../../models/workflow-trigger-node';

/**
 * Composant utilisé pour le rendu d'un noeud correspondant à un trigger de workflow.
 */
@Component({
    selector: 'app-workflow-trigger-node',
    imports: [ReteModule, CommonModule],
    templateUrl: './workflow-trigger-node.component.html',
    styleUrl: './workflow-trigger-node.component.scss',
    host: {
        'data-testid': 'node',
    }
})
export class WorkflowTriggerNodeComponent implements OnChanges {
    /**
     * Données du noeud.
     */
    private _data!: WorkflowTriggerNode;

    /**
     * Données du trigger de workflow affiché par ce noeud.
     */
    public workflowTriggerData: object;

    /**
     * Spécifie les données du noeud.
     */
    @Input()
    set data(value: WorkflowTriggerNode) {
        this._data = value;
        this.retrieveWorkflowTriggerData();
    }

    /**
     * Obtient les données du noeud
     */
    get data(): WorkflowTriggerNode {
        return this._data;
    }

    /**
     * Emet des évènements génrérés par les sockets (Input, Output) du noeud et ses contrôles.
     */
    @Input() emit!: (data: any) => void;

    /**
     * Fonction a appeler lorsque le rendu du noeud est terminé (facultatif).
     */
    @Input() rendered!: () => void;

    // TODO : verifier si necessaire
    seed = 0;

    /**
     * Valeur indiquant l'état de selection précedant du noeud (true : selectionné, false : deselectionné).
     */
    previousSelectedState = undefined;

    /**
     * Obtient une valeur indiquant si le noeud est selectionné.
     * Associe cette valeur à la classe selected --> TODO : verifier si c'est toujours le cas.
     */
    @HostBinding('class.selected') get selected() {
        return this.data.selected;
    }

    /**
     * Constructeur.
     * @param cdr : Reference au detecteur de changement Angular.
     */
    constructor(
        private cdr: ChangeDetectorRef,
        private host: ElementRef
    ) {}

    /**
     * Appellé lorsqu'un changement est detecté sur le composant.
     */
    ngOnChanges(): void {
        this.retrieveWorkflowTriggerData();

        this.cdr.detectChanges();
        requestAnimationFrame(() => this.rendered());
        // TODO : verifier si necessaire
        this.seed++; // force render sockets

        this.emitSelectionChange();
    }

    /**
     * Emet une valeur indiquant que l'état de selection du noeud a changé.
     */
    private emitSelectionChange() {
        // Si une fonction de callback est spécifié, execute la fonction
        if (this.data.callBack) {
            if (this.previousSelectedState !== this.data.selected) {
                if (this.data.selected === true) {
                    this.data.callBack(this.data.id, new NodeEvent(NodeEventTypes.Selected));
                    ((this.host.nativeElement as HTMLElement).querySelector('#node-container') as HTMLDivElement).focus();
                } else if (this.data.selected === false) {
                    this.data.callBack(this.data.id, new NodeEvent(NodeEventTypes.Unselected));
                }
                this.previousSelectedState = this.data.selected;
            }
        }
    }

    /**
     * Récupère les données du trigger de workflow.
     */
    private retrieveWorkflowTriggerData() {
        if (this._data) {
            this.workflowTriggerData = this._data.workflowTriggerData;
        } else {
            this.workflowTriggerData = null;
        }
    }

    /**
     * Fonction utilisée pour trier les noeuds par rapport à leurs indexs
     * @param a
     * @param b
     * @returns
     */
    sortByIndex<N extends object, T extends KeyValue<string, N & { index?: number }>>(a: T, b: T) {
        const ai = a.value.index || 0;
        const bi = b.value.index || 0;

        return ai - bi;
    }
}
